<!-- src/components/forms/completed/SubContractorPackCompleted.vue -->
<template>
  <div class="w-full space-y-12 animate-fadeIn text-left pb-24 printable-content">
    
    <!-- 1. GENERAL - COMPANY INFORMATION -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <IdentificationIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">1. General - Company Information</h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50 p-8 rounded-[40px] border border-slate-100 shadow-inner text-left">
        <div class="space-y-6">
          <div class="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm">
            <p class="micro-label text-[#20FCF1] mb-3">Corporate Identity</p>
            <h3 class="text-2xl font-black text-[#0A1630] uppercase leading-none">{{ companyData.companyName }}</h3>
            <div class="flex flex-wrap gap-4 mt-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">
              <span class="bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100">Reg: {{ companyData.regNumber }}</span>
              <span v-if="companyData.vat" class="bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100">VAT: {{ companyData.vat }}</span>
              <span v-if="companyData.utr" class="bg-[#EBFDF8] text-[#20FCF1] px-3 py-1.5 rounded-xl border border-[#20FCF1]/20">UTR: {{ companyData.utr }}</span>
            </div>
          </div>
          <div class="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm min-h-[120px]">
            <p class="micro-label text-slate-400 mb-3">Registered Office</p>
            <p class="text-sm text-slate-700 italic leading-relaxed font-medium">{{ companyData.companyAddress }}</p>
          </div>
        </div>

        <div class="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm relative overflow-hidden">
          <div class="absolute top-0 right-0 p-6 opacity-5"><GlobeEuropeAfricaIcon class="h-24 w-24" /></div>
          <p class="micro-label text-[#20FCF1] mb-6">Authorised Representation</p>
          <div class="space-y-1">
             <p class="text-lg font-black text-[#0A1630] uppercase leading-none">{{ companyData.contactName }}</p>
             <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{{ companyData.contactPosition }}</p>
          </div>
          <div class="space-y-3 pt-6 mt-6 border-t border-slate-50">
            <div class="flex justify-between items-center bg-slate-50 p-3 rounded-xl border border-slate-100">
               <span class="text-[9px] font-black text-slate-400 uppercase">Email Protocol</span>
               <span class="text-xs font-black text-[#0A1630] lowercase">{{ companyData.contactEmail }}</span>
            </div>
            <div class="flex justify-between items-center bg-slate-50 p-3 rounded-xl border border-slate-100">
               <span class="text-[9px] font-black text-slate-400 uppercase">Secure Line</span>
               <span class="text-xs font-black text-[#0A1630]">{{ companyData.contactPhone }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 1.2. SERVICES & QUALITY ASSURANCE -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <CheckBadgeIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">1.2. Services & Quality</h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="StatCard bg-white border-l-4 border-[#20FCF1]">
          <span class="label">Railway Undertaking</span>
          <span class="value uppercase">{{ formatYesNoNA(railwayData.undertakesWork) }}</span>
          <p v-if="railwayData.serviceSummary" class="mt-4 text-[10px] text-slate-500 font-medium italic leading-relaxed">{{ railwayData.serviceSummary }}</p>
        </div>
        <div class="StatCard bg-white">
          <span class="label">RISQS Status</span>
          <span class="value uppercase">{{ formatYesNoNA(submissionData.risqsStatus?.isApproved || submissionData.formData?.risqsStatus?.isApproved) }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">ISO 9001 Alignment</span>
          <span class="value uppercase">{{ formatYesNoNA(submissionData.qualityAssurance?.isCertified || submissionData.formData?.qualityAssurance?.isCertified) }}</span>
        </div>
      </div>
    </section>

    <!-- 2. MANAGEMENT OF HEALTH AND SAFETY -->
    <section class="pdf-page-break">
      <div class="flex items-center gap-4 mb-8 text-left">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <ShieldCheckIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">2. Safety Governance</h2>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div class="bg-slate-50 p-8 rounded-[40px] border border-slate-100 shadow-inner space-y-4">
          <div class="flex justify-between items-center bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
             <span class="text-[10px] font-black uppercase text-slate-400 tracking-widest">EMS (ISO 14001) Alignment</span>
             <span :class="getStatusClass(submissionData.environmental?.isCertified || submissionData.formData?.environmental?.isCertified)" class="Badge">{{ formatYesNoNA(submissionData.environmental?.isCertified || submissionData.formData?.environmental?.isCertified) }}</span>
          </div>
          <div v-for="subKey in ['refuseToWork', 'alcoholDrugs', 'modernSlavery']" :key="subKey" class="flex justify-between items-center bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
             <span class="text-[10px] font-black uppercase text-slate-400 tracking-widest">{{ subKey.replace(/([A-Z])/g, ' $1') }} Policy</span>
             <span :class="getStatusClass(submissionData.hsManagement?.[subKey] || submissionData.formData?.hsManagement?.[subKey])" class="Badge">{{ formatYesNoNA(submissionData.hsManagement?.[subKey] || submissionData.formData?.hsManagement?.[subKey]) }}</span>
          </div>
        </div>
        
        <div class="bg-[#0A1630] p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden flex flex-col justify-center">
          <div class="absolute top-0 right-0 p-8 opacity-5"><ExclamationTriangleIcon class="h-40 w-40" /></div>
          <div class="relative z-10">
            <p class="micro-label text-[#20FCF1] mb-6 uppercase tracking-[0.4em]">Safety Performance Metrics</p>
            <div class="space-y-6">
              <div>
                <p class="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Fatalities / RIDDORS (3 Years)</p>
                <p class="text-sm font-medium leading-relaxed italic text-slate-300 bg-white/5 p-5 rounded-2xl border border-white/5">{{ submissionData.hsManagement?.riddorsDetails || submissionData.formData?.hsManagement?.riddorsDetails || 'No RIDDORS reported.' }}</p>
              </div>
              <div>
                <p class="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Accident Statistics (5 Years)</p>
                <p class="text-sm font-black text-white bg-white/5 p-5 rounded-2xl border border-white/10">{{ submissionData.hsManagement?.accidentStats || submissionData.formData?.hsManagement?.accidentStats || 'No data provided.' }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 2.2. INSURANCE POLICIES -->
    <section class="pdf-page-break">
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <ClipboardDocumentCheckIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2.2. Insurance Portfolio</h2>
      </div>

      <div class="overflow-x-auto rounded-[40px] border border-slate-200 shadow-2xl bg-white overflow-hidden">
        <table class="min-w-full divide-y divide-slate-200">
          <thead class="bg-[#0A1630]">
            <tr>
              <th class="px-8 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Insurance Type</th>
              <th class="px-8 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest text-center">Status</th>
              <th class="px-8 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Insurer</th>
              <th class="px-8 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Limit (£)</th>
              <th class="px-8 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Expiry</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <tr v-for="(label, key) in insuranceMapping" :key="key" class="hover:bg-slate-50 transition-colors">
              <td class="px-8 py-5 text-[11px] font-black text-slate-700 uppercase bg-slate-50/50 min-w-[200px]">{{ label }}</td>
              <td class="px-8 py-5 text-center">
                <span :class="(submissionData.insuranceGrid?.[key]?.held || submissionData.formData?.insuranceGrid?.[key]?.held) === true ? 'text-emerald-600 bg-emerald-50' : 'text-slate-400 bg-slate-100'" class="px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border border-black/5">
                  {{ formatYesNoNA(submissionData.insuranceGrid?.[key]?.held || submissionData.formData?.insuranceGrid?.[key]?.held) }}
                </span>
              </td>
              <td class="px-8 py-5 text-xs font-bold text-slate-700 uppercase tracking-tight">{{ submissionData.insuranceGrid?.[key]?.insurer || submissionData.formData?.insuranceGrid?.[key]?.insurer || '—' }}</td>
              <td class="px-8 py-5 text-xs font-mono font-black text-[#0A1630]">{{ submissionData.insuranceGrid?.[key]?.limit || submissionData.formData?.insuranceGrid?.[key]?.limit || '—' }}</td>
              <td class="px-8 py-5 text-xs font-black text-rose-500">{{ formatDate(submissionData.insuranceGrid?.[key]?.expiry || submissionData.formData?.insuranceGrid?.[key]?.expiry, 'dateOnly') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 4. MANUAL SUPPLIER EVALUATION -->
    <section v-if="submissionData.step3Bypass === false || submissionData.formData?.step3Bypass === false" class="pdf-page-break">
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <MagnifyingGlassIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none text-left">4. Manual Evaluation Matrix</h2>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div v-for="(cat, catIdx) in s4Structure" :key="catIdx" class="space-y-4 bg-slate-50/50 p-8 rounded-[40px] border border-slate-100 shadow-inner">
            <h4 class="text-[10px] font-black uppercase tracking-[0.4em] text-[#20FCF1] mb-6">{{ cat.title }}</h4>
            <div v-for="q in cat.questions" :key="q.key" class="flex items-center justify-between p-5 bg-white border border-slate-200 rounded-2xl shadow-sm">
              <p class="text-[10px] font-bold text-slate-600 uppercase tracking-tight max-w-[75%]">{{ q.text }}</p>
              <span :class="getStatusClass(getNestedValue(submissionData.s3 || submissionData.formData?.s3, `${cat.key}.${q.key}`))" class="Badge border border-black/5">
                {{ formatYesNoNA(getNestedValue(submissionData.s3 || submissionData.formData?.s3, `${cat.key}.${q.key}`)) }}
              </span>
            </div>
        </div>
      </div>
    </section>

    <!-- 5. DOCUMENT CHECKLIST -->
    <section class="pdf-page-break">
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <DocumentTextIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">5. Document Verification</h2>
      </div>
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 bg-slate-50 p-10 rounded-[40px] shadow-inner document-checklist-grid">
        <div v-for="(label, key) in checklistLabels" :key="key" 
             class="flex items-start gap-4 p-5 rounded-2xl border transition-all doc-checklist-card"
             :class="(submissionData.documents?.[key] || submissionData.formData?.documents?.[key]) ? 'bg-[#EBFDF8] border-[#20FCF1] text-[#0A1630] shadow-md doc-checked' : 'bg-white border-slate-100 opacity-40 grayscale scale-95 doc-unchecked'">
          <CheckCircleIcon v-if="submissionData.documents?.[key] || submissionData.formData?.documents?.[key]" class="h-5 w-5 text-[#20FCF1] flex-shrink-0 mt-0.5" />
          <XCircleIcon v-else class="h-5 w-5 text-slate-300 flex-shrink-0 mt-0.5" />
          <span class="text-[9px] font-black uppercase tracking-tighter leading-tight">{{ label }}</span>
        </div>
      </div>
    </section>

    <!-- 6. SECURE EVIDENCE VAULT -->
    <section v-if="hasEvidence" class="pdf-page-break">
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 print:flex-col print:items-start print:justify-start">
        <div class="flex items-center gap-4 text-left">
          <div class="bg-[#0A1630] text-[#20FCF1] w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
            <PaperClipIcon class="h-7 w-7" />
          </div>
          <div class="text-left">
            <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">6. Secure Evidence Vault</h2>
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 text-left">Direct Cloud Link Repository</p>
          </div>
        </div>
        <button @click="downloadAllFiles" class="no-print bg-[#20FCF1] text-[#0A1630] px-10 py-5 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-3">
          <CloudArrowDownIcon class="h-5 w-5" />
          <span>Batch Protocol Download</span>
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div v-for="(files, category) in categorizedEvidence" :key="category" class="bg-white p-10 rounded-[40px] border border-slate-200 shadow-xl group hover:border-[#20FCF1] relative">
          <div class="flex items-center gap-4 mb-8">
            <div class="w-2 h-8 bg-[#20FCF1] rounded-full group-hover:scale-y-110 transition-transform"></div>
            <p class="text-xs font-black uppercase tracking-[0.2em] text-[#0A1630]">{{ formatCategoryLabel(category) }}</p>
          </div>
          
          <div class="space-y-4">
            <div v-for="(url, idx) in files" :key="idx" class="flex items-center justify-between p-5 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-white hover:border-[#20FCF1] transition-all shadow-inner">
               <div class="flex items-center gap-4 min-w-0">
                  <DocumentIcon v-if="!isImage(url)" class="h-6 w-6 text-[#20FCF1] shrink-0" />
                  <PhotoIcon v-else class="h-6 w-6 text-[#20FCF1] shrink-0" />
                  <div class="min-w-0">
                     <p class="text-[10px] font-black text-[#0A1630] uppercase truncate max-w-[200px]">{{ getFileNameFromUrl(url) }}</p>
                     <p class="text-[8px] font-bold text-slate-400 uppercase mt-1">Registry Validated</p>
                  </div>
               </div>
               <a :href="url" target="_blank" class="bg-white p-3 rounded-xl border border-slate-200 text-[#0A1630] hover:bg-[#0A1630] hover:text-white transition-all shadow-sm">
                  <ArrowTopRightOnSquareIcon class="h-4 w-4" />
               </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 🛡️ 7. AUTHORITATIVE SUBMISSION SEAL (CALIBRATED ALIGNMENT) -->
    <section class="pdf-page-break space-y-10">
      <div class="flex items-center gap-4">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <CheckBadgeIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">7. Authoritative Submission Seal</h2>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch signature-block-grid">
        <!-- ATTESTATION BLOCK -->
        <div class="p-12 bg-[#0A1630] rounded-[48px] border-b-8 border-slate-800 shadow-2xl relative overflow-hidden flex items-center attestation-card">
          <div class="absolute -left-10 -top-10 opacity-5"><ShieldCheckIcon class="h-64 w-64 text-[#20FCF1]" /></div>
          <p class="text-base text-slate-300 font-medium leading-relaxed italic relative z-10">"I formally attest that the responses and documentation provided in this corporate onboarding pack are true, accurate, and valid to the best of my knowledge as representing the registered legal entity."</p>
        </div>

        <!-- IDENTITY BLOCK -->
        <div v-if="submissionData.declaration?.signatureDataUrl || submissionData.signatureDataUrl || submissionData.signatureUrl" class="bg-[#0A1630] p-10 rounded-[48px] border-b-8 border-[#20FCF1] shadow-2xl flex flex-col items-center justify-center text-center signature-card">
          <p class="micro-label text-[#20FCF1] mb-8 uppercase tracking-[0.4em]">Digital Entity Seal</p>
          <div class="p-8 bg-white rounded-[32px] shadow-inner mb-8 w-full flex justify-center signature-seal-container">
             <img :src="submissionData.declaration?.signatureDataUrl || submissionData.signatureDataUrl || submissionData.signatureUrl" alt="Registry Seal" class="max-h-24 object-contain grayscale brightness-75 signature-image" />
          </div>
          <div class="space-y-2">
            <p class="text-lg font-black uppercase text-white tracking-tight leading-none">{{ submissionData.declaration?.name || companyData.contactName || 'Corporate Lead' }}</p>
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">{{ submissionData.declaration?.position || companyData.contactPosition || 'Authorised Rep' }}</p>
            <div class="flex items-center justify-center gap-2 mt-6 pt-6 border-t border-white/5 w-full text-[#20FCF1] signature-timestamp-row">
               <CheckBadgeIcon class="h-4 w-4" />
               <span class="text-[10px] font-black uppercase tracking-[0.2em]">Identity Handshake Verified: {{ formatDate(submissionData.submittedAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 🛡️ 8. STAGE 2: MANAGEMENT APPRAISAL (FLOATING MINT DESIGN) -->
    <section v-if="hasAppraisalAccess || appraisalData.appraisedBy" class="pt-20 border-t-8 border-[#0A1630] pdf-page-break space-y-10">
      <div class="flex items-center gap-4">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <ShieldCheckIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">8. Management Appraisal Registry</h2>
      </div>

      <div class="bg-[#0A1630] p-10 md:p-14 rounded-[50px] text-white shadow-2xl relative overflow-hidden border-b-[12px] border-[#20FCF1] appraisal-box">
        <div class="absolute top-0 right-0 p-12 opacity-5"><ShieldCheckIcon class="h-64 w-64" /></div>
        
        <div class="relative z-10">
          <div class="flex flex-col md:flex-row items-start md:items-center justify-between mb-16 gap-8 appraisal-header">
            <div>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.4em] mb-4">Formal Internal Evaluation • Office Use Protocol</p>
              <h2 class="text-3xl md:text-4xl font-black uppercase tracking-tight leading-none">Internal Compliance Audit</h2>
            </div>
            <div v-if="appraisalData.status" :class="statusClass(appraisalData.status)" class="px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl border border-white/10 backdrop-blur-md appraisal-status-badge">
              {{ appraisalData.status }}
            </div>
          </div>

          <div class="space-y-6 mb-16">
            <div v-for="(item, idx) in appraisalChecklist" :key="idx" 
                 class="flex flex-col lg:flex-row items-start lg:items-center justify-between p-8 bg-white/5 border border-white/10 rounded-[32px] hover:bg-white/[0.08] transition-all group appraisal-row">
              <div class="flex items-center gap-6 mb-6 lg:mb-0">
                 <div class="w-12 h-12 rounded-2xl bg-[#20FCF1] text-[#0A1630] flex items-center justify-center font-black text-xl shadow-lg group-hover:scale-110 transition-transform appraisal-index">{{ idx + 1 }}</div>
                 <p class="text-sm font-bold uppercase tracking-tight text-slate-200 max-w-2xl leading-relaxed appraisal-text">{{ item.text }}</p>
              </div>
              <div class="flex items-center gap-4 ml-16 lg:ml-0 appraisal-buttons">
                <button type="button" @click="toggleAppraisal(item.key, true)" :disabled="!hasAppraisalAccess" :class="appraisalData[item.key] === true ? 'bg-[#20FCF1] text-[#0A1630] scale-105 shadow-xl shadow-[#20FCF1]/20' : 'bg-white/5 text-slate-500 border border-white/10'" class="px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all">Yes</button>
                <button type="button" @click="toggleAppraisal(item.key, false)" :disabled="!hasAppraisalAccess" :class="appraisalData[item.key] === false ? 'bg-rose-500 text-white scale-105 shadow-xl shadow-rose-500/20' : 'bg-white/5 text-slate-500 border border-white/10'" class="px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all">No</button>
                <button v-if="item.allowNA" type="button" @click="toggleAppraisal(item.key, 'na')" :disabled="!hasAppraisalAccess" :class="appraisalData[item.key] === 'na' ? 'bg-amber-500 text-[#0A1630] scale-105 shadow-xl shadow-amber-500/20' : 'bg-white/5 text-slate-500 border border-white/10'" class="px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all">N/A</button>
              </div>
            </div>
          </div>

          <div class="space-y-4">
            <label class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] ml-2">Appraisal Notes & Directives</label>
            <textarea v-model="appraisalData.comments" :readonly="!hasAppraisalAccess" rows="5" class="w-full bg-white/5 border border-white/10 rounded-[40px] p-10 text-base font-medium text-slate-200 focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all shadow-inner placeholder:text-white/10 appraisal-comments" placeholder="Enter internal synthesis..."></textarea>
          </div>

          <!-- 🛡️ MANAGEMENT AUDIT SEAL -->
          <div v-if="appraisalData.appraisedBy" class="mt-12 pt-12 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-8 animate-fadeIn appraisal-audit-seal">
             <div class="flex items-center gap-6">
                <div class="h-20 w-20 bg-white p-4 rounded-[24px] shadow-2xl border-4 border-[#20FCF1]/20 flex items-center justify-center appraisal-seal-icon">
                   <ShieldCheckIcon class="h-10 w-10 text-[#20FCF1]" />
                </div>
                <div>
                   <p class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mb-1">Authoritative Audit Seal</p>
                   <p class="text-xl font-black text-white uppercase tracking-tight">Identity Verified: {{ appraisalData.appraisedBy }}</p>
                   <p class="text-[11px] font-bold text-slate-500 uppercase mt-1 tracking-widest">{{ formatDate(appraisalData.appraisedAt) }}</p>
                </div>
             </div>
             
             <div v-if="hasAppraisalAccess" class="flex flex-wrap justify-center gap-4 no-print">
               <button v-for="status in ['Reviewed', 'In Progress', 'Requires Action']" :key="status" @click="updateStatus(status)" :class="appraisalData.status === status ? 'bg-[#20FCF1] text-[#0A1630] ring-4 ring-[#20FCF1]/30' : 'bg-white/10 text-white hover:bg-white/20'" class="px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-2xl">{{ status }}</button>
             </div>
          </div>

          <div v-if="hasAppraisalAccess" class="mt-12 flex justify-end no-print">
            <button @click="saveAppraisal" :disabled="isSaving" class="bg-[#20FCF1] text-[#0A1630] px-16 py-6 rounded-[32px] font-black uppercase tracking-[0.2em] text-sm shadow-2xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 min-w-[320px]">
              <span v-if="isSaving" class="flex items-center gap-3"><div class="animate-spin h-5 w-5 border-2 border-[#0A1630] border-t-transparent rounded-full"></div> Synchronising Registry...</span>
              <span v-else>Apply Management Seal</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { db } from '@/firebaseConfig';
import { doc, updateDoc } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';
import { 
  ShieldCheckIcon, ClipboardDocumentCheckIcon, DocumentTextIcon, CheckBadgeIcon, 
  ExclamationTriangleIcon, DocumentIcon, PaperClipIcon, IdentificationIcon,
  GlobeEuropeAfricaIcon, CloudArrowDownIcon, ArrowTopRightOnSquareIcon,
  PhotoIcon, CheckCircleIcon, XCircleIcon, MagnifyingGlassIcon
} from '@heroicons/vue/24/outline';

const props = defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true },
  formatText: { type: Function, required: true },
  formatYesNoNA: { type: Function, required: true },
  getStatusClass: { type: Function, required: true },
  isImage: { type: Function, required: true }
});

const route = useRoute();
const userStore = useUserStore();
const isSaving = ref(false);

const appraisalData = reactive({
  complianceLevel: null, creditChecks: null, auditNeeded: null,
  insurancesMatch: null, piSufficient: null, cyberTraining: null,
  approveList: null, comments: '', status: 'Pending Review',
  appraisedBy: '', appraisedAt: null
});

const hasAppraisalAccess = computed(() => {
  const role = userStore.role;
  const permissions = userStore.claims?.permissions || {};
  return role === 'admin' || role === 'super-admin' || permissions.canManageOnboarding === true || permissions.canViewOnboarding === true;
});

const downloadAllFiles = () => {
    const allUrls = Object.values(categorizedEvidence.value).flat();
    if (allUrls.length === 0) return;
    confirm(`Confirm: Sequential download of all ${allUrls.length} corporate protocol files?`);
    allUrls.forEach((url, index) => {
        setTimeout(() => {
            const link = document.createElement('a');
            link.href = url; link.target = '_blank'; link.download = getFileNameFromUrl(url);
            document.body.appendChild(link); link.click(); document.body.removeChild(link);
        }, index * 800);
    });
};

const getFileNameFromUrl = (url) => {
    if (!url) return "Protocol_File.pdf";
    try {
        const decoded = decodeURIComponent(url);
        const parts = decoded.split('/');
        const fileNamePart = parts[parts.length - 1].split('?')[0];
        return fileNamePart.replace(/^\d+_/, '');
    } catch (e) { return "Registry_File.pdf"; }
};

const appraisalChecklist = [
  { key: 'complianceLevel', text: 'Has the Consultant/Sub-Contractor demonstrated a satisfactory level of compliance overall?' },
  { key: 'creditChecks', text: 'Financial and credit evaluations have been successfully completed on the entity providing services or products.' },
  { key: 'auditNeeded', text: 'Is an initial corporate audit or formal site inspection required for this specific contractor?' },
  { key: 'insurancesMatch', text: 'Do the insurance policies provided align with the risk profile of the services offered?' },
  { key: 'piSufficient', text: 'Is the Professional Indemnity cover adequate for the intended scope of work?', allowNA: true },
  { key: 'cyberTraining', text: 'Is additional data security briefing or company cyber policy training required for this entity?' },
  { key: 'approveList', text: 'Should this Consultant/Sub-Contractor be formalised onto the corporate Approved Supplier List?' }
];

const toggleAppraisal = (key, val) => {
    if (!hasAppraisalAccess.value) return;
    if (appraisalData[key] === val) appraisalData[key] = null;
    else appraisalData[key] = val;
};

const updateStatus = (status) => { if (hasAppraisalAccess.value) appraisalData.status = status; };

const saveAppraisal = async () => {
    if (isSaving.value || !hasAppraisalAccess.value) return;
    isSaving.value = true;
    try {
        const subId = props.submissionData?.id || props.submissionData?.docId || route.params.submissionId;
        const docRef = doc(db, 'onboardingFormSubmissions', subId);
        const profile = userStore.userProfile || {};
        const adminName = profile.firstName ? `${profile.firstName} ${profile.lastName}`.trim() : 'Authorising Manager';
        const timestamp = new Date().toISOString();
        await updateDoc(docRef, { 
            internalAppraisal: { ...appraisalData, appraisedBy: adminName, appraisedAt: timestamp },
            status: appraisalData.status,
            'formData.status': appraisalData.status
        });
        appraisalData.appraisedBy = adminName;
        appraisalData.appraisedAt = timestamp;
        alert("Corporate Registry Synchronised Successfully.");
    } catch (e) { alert(`Registry Write Failed: ${e.message}`); } finally { isSaving.value = false; }
};

onMounted(() => { if (props.submissionData?.internalAppraisal) Object.assign(appraisalData, props.submissionData.internalAppraisal); });

const companyData = computed(() => {
    const s = props.submissionData;
    const info = s?.companyInfo || s?.formData?.companyInfo || {};
    return {
        companyName: info.companyName || s?.companyName || s?.formData?.companyName || 'Unknown Entity',
        regNumber: info.regNumber || s?.regNumber || s?.formData?.regNumber || 'N/A',
        vat: info.vat || s?.vat || s?.formData?.vat || null,
        utr: info.utr || s?.utr || s?.formData?.utr || null,
        companyAddress: info.companyAddress || s?.companyAddress || s?.formData?.companyAddress || 'N/A',
        contactName: info.contactName || s?.contactName || s?.formData?.contactName || 'Registry Lead',
        contactPosition: info.contactPosition || s?.contactPosition || s?.formData?.contactPosition || 'Representative',
        contactEmail: info.contactEmail || s?.contactEmail || s?.email || s?.formData?.email || 'N/A',
        contactPhone: info.contactPhone || s?.contactPhone || s?.formData?.contactPhone || 'N/A',
        website: info.website || s?.website || s?.formData?.website || null
    };
});

const railwayData = computed(() => {
    const s = props.submissionData;
    const rw = s?.railwaySector || s?.formData?.railwaySector || {};
    return {
        undertakesWork: rw.undertakesWork ?? s?.undertakesWork ?? s?.formData?.undertakesWork ?? null,
        serviceSummary: rw.serviceSummary || s?.serviceSummary || s?.formData?.serviceSummary || null
    };
});

const categoryMapping = {
  evidence_risqsCertificate: 'RISQS Approved Status', evidence_iso9001Certificate: 'ISO 9001 Certification',
  evidence_environmentalCertificate: 'ISO 14001 / EMS Evidence', evidence_refuseToWorkPolicy: 'Refuse to Work Policy',
  evidence_alcoholDrugsPolicy: 'Alcohol & Drugs Protocol', evidence_modernSlaveryPolicy: 'Modern Slavery Compliance',
  evidence_insurancePolicies: 'Registry Insurance Portfolio', evidence_checklistAdditional: 'Supporting Documentation Vault'
};

const categorizedEvidence = computed(() => {
    const s = props.submissionData;
    const evidence = {};
    if (!s) return evidence;
    Object.keys(categoryMapping).forEach(key => {
        const files = s[key] || s.formData?.[key] || s.submissionData?.[key];
        if (Array.isArray(files) && files.length > 0) evidence[key] = files;
    });
    return evidence;
});

const hasEvidence = computed(() => Object.keys(categorizedEvidence.value).length > 0);
const formatCategoryLabel = (key) => categoryMapping[key] || 'Corporate Registry Files';
const insuranceMapping = { contractors: 'Contractors (all risks)', employers: 'Employers Liability', other: 'Other Insurance', plant: 'Plant', products: 'Products Liability', professional: 'Professional Indemnity', public: 'Public Liability' };

const checklistLabels = {
  orgChart: 'Organisation Chart', isoCertification: 'ISO Certs', risqsCertificate: 'RISQS Cert',
  hsPolicy: 'H&S Policy', qualityPolicy: 'Quality Policy', environmentPolicy: 'Env Policy',
  alcoholDrugsPolicy: 'A&D Policy', workSafePolicy: 'Work Safe', workingHoursProcedure: 'Work Hours',
  equalityDiversityPolicy: 'Diversity', insurances: 'Insurances', othersNotices: 'Notices',
  hsAdvisorDetails: 'Advisor CV', cyberSecurity: 'Cyber Security'
};

const s4Structure = [
  { key: 'training', title: 'Training & Induction', questions: [{key:'induction', text:'Induction training provided?'}, {key:'recordsMaintained', text:'Records maintained?'}, {key:'requirementsReviewed', text:'Requirements reviewed?'}]},
  { key: 'quality', title: 'Quality Assurance', questions: [{key:'policyStatement', text:'Quality policy statement active?'}, {key:'manualProcedures', text:'Manual & Procedures available?'}, {key:'reviewedAudited', text:'Regularly reviewed/audited?'}]},
  { key: 'documentControl', title: 'Document Control', questions: [{key:'revisionStatus', text:'Numbering & Revision status used?'}, {key:'checkingSystem', text:'System for approving outgoing docs?'}, {key:'filingSystem', text:'Transmittal/Filing system operation?'}]},
  { key: 'hsManagement', title: 'Health & Safety', questions: [{key:'policyStatement', text:'H&S Policy Statement held?'}, {key:'reviewProcedures', text:'Procedures for reviewing system?'}, {key:'riskAssessment', text:'Risk Assessment process active?'}]},
  { key: 'compliance', title: 'Compliance', questions: [{key:'hasPerson', text:'Appointed person for compliance?'}]}
];

const getNestedValue = (obj, path) => {
    if (!obj) return null;
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const statusClass = (s) => {
  const status = String(s || '').toUpperCase();
  if (status === 'REVIEWED') return 'bg-[#20FCF1] text-[#0A1630] border-none';
  if (status === 'REQUIRES ACTION') return 'bg-rose-500 text-white border-none';
  return 'bg-amber-500 text-[#0A1630] border-none';
};
</script>

<style scoped>
.StatCard { @apply flex flex-col p-8 rounded-[32px] border border-slate-200 shadow-sm transition-all text-left; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-[0.2em] mb-2 text-left; }
.StatCard .value { @apply text-base font-black truncate text-[#0A1630] text-left; }
.Badge { @apply px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest flex-shrink-0; }
.micro-label { @apply block text-[10px] font-black uppercase tracking-[0.3em] leading-none text-left; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

/* 🛡️ PDF PAGE BREAK PROTOCOL */
@media print {
  .pdf-page-break { break-before: page; margin-top: 2rem; }
  .no-print { display: none !important; }
}

table { border-collapse: separate; border-spacing: 0; }

/* 🛡️ HIGH-FIDELITY PRINT RENDERING SYSTEM */
@media print {
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  /* Prevent sections from splitting awkwardly across sheets */
  section {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    margin-bottom: 2.5rem !important;
  }

  .appraisal-row, .doc-checklist-card, .StatCard, tr, .signature-card, .attestation-card {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  /* Force display grids back to correct structural layout on print */
  .grid {
    display: grid !important;
  }
  
  .grid-cols-1.md:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 1.5rem !important;
  }

  .grid-cols-1.md:grid-cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 1.25rem !important;
  }

  .document-checklist-grid {
    display: grid !important;
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    gap: 0.75rem !important;
    padding: 1.5rem !important;
    background-color: #F8FAFC !important;
  }

  .signature-block-grid {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 1.5rem !important;
  }

  /* Visual Contrast Fixes for Checklist items */
  .doc-checklist-card {
    opacity: 1 !important;
    transform: none !important;
    filter: none !important;
  }

  .doc-unchecked {
    background-color: #FFFFFF !important;
    border-color: #E2E8F0 !important;
    color: #94A3B8 !important;
  }

  .doc-checked {
    background-color: #EBFDF8 !important;
    border-color: #20FCF1 !important;
    color: #0A1630 !important;
  }

  /* Safeguard Digital Seal trace sizing */
  .signature-image {
    max-height: 70px !important;
    width: auto !important;
    display: block !important;
    margin: 0 auto !important;
  }

  .signature-seal-container {
    padding: 1rem !important;
    background-color: #FFFFFF !important;
  }

  /* Clean table borders on physical outputs */
  table {
    width: 100% !important;
  }
  
  th, td {
    padding: 0.75rem 1rem !important;
  }

  /* Manage appraisal view when printing */
  .appraisal-box {
    border-radius: 24px !important;
    padding: 2rem !important;
    background-color: #0A1630 !important;
  }

  .appraisal-row {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 1rem !important;
    margin-bottom: 0.75rem !important;
    border-radius: 16px !important;
  }

  .appraisal-buttons {
    display: flex !important;
    flex-direction: row !important;
    gap: 0.5rem !important;
    margin-left: 0 !important;
  }

  .appraisal-comments {
    border-radius: 16px !important;
    padding: 1.5rem !important;
  }
}
</style>